/**
 * Pagination control: Previous / "Page X of Y" / Next, in a centered flex row.
 * Accent-filled buttons; disabled at the first/last page.
 */

export interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  /** Alias for `onPageChange`. */
  onChange?: (page: number) => void;
  /** Hide the "Page X of Y" label. */
  hideLabel?: boolean;
  /** Backward-compat: some features pass `showPageInfo` instead. */
  showPageInfo?: boolean;
  className?: string;
}

const BTN_CLASS =
  'px-4 py-2 rounded-md border-none bg-[color:var(--accent)] text-white cursor-pointer ' +
  'font-[var(--font-body)] transition-colors hover:bg-[color:var(--accent-hover)] ' +
  'disabled:opacity-50 disabled:cursor-default disabled:hover:bg-[color:var(--accent)]';

export function Pagination({
  page,
  totalPages,
  onPageChange,
  onChange,
  hideLabel = false,
  showPageInfo,
  className,
}: PaginationProps): JSX.Element {
  const handle = onPageChange ?? onChange ?? (() => undefined);
  const hide = hideLabel || showPageInfo === false;
  const safeTotal = Math.max(1, totalPages);
  const clampedPage = Math.min(Math.max(1, page), safeTotal);

  const prevDisabled = clampedPage <= 1;
  const nextDisabled = clampedPage >= safeTotal;

  return (
    <div
      className={['flex items-center justify-center gap-4 mb-8', className]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        onClick={() => handle(clampedPage - 1)}
        disabled={prevDisabled}
        aria-label="Previous page"
        className={BTN_CLASS}
      >
        &larr; Previous
      </button>
      {hide ? null : (
        <span
          aria-live="polite"
          className="text-[color:var(--text-dark)] min-w-[6rem] text-center font-[var(--font-body)]"
        >
          Page {clampedPage} of {safeTotal}
        </span>
      )}
      <button
        type="button"
        onClick={() => handle(clampedPage + 1)}
        disabled={nextDisabled}
        aria-label="Next page"
        className={BTN_CLASS}
      >
        Next &rarr;
      </button>
    </div>
  );
}

export default Pagination;
